import React, {useState} from "react";
import {Box, ButtonBase, IconButton, Menu, MenuItem, Tooltip, Typography} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const WorkspaceAvatar = ({name, size = 32}) => (
    <Box sx={{
        width: size,
        height: size,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "8px",
        backgroundColor: "var(--app-bg-subtle)",
        color: "var(--app-text-primary)",
        fontFamily: "Inter",
        fontSize: size <= 30 ? "13px" : "14px",
        fontWeight: 650,
    }}>
        {(name || "W").trim().charAt(0).toUpperCase() || "W"}
    </Box>
);

const WorkspaceSwitcher = ({collapsed, workspaces, currentWorkspaceId, onSelect}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const supportedWorkspaces = (Array.isArray(workspaces) ? workspaces : [])
        .filter((workspace) => workspace?.role === "owner" || workspace?.role === "editor");
    const activeWorkspace = supportedWorkspaces.find(
        (workspace) => workspace.workspaceId === currentWorkspaceId
    ) || supportedWorkspaces[0];

    if (!activeWorkspace) return null;

    const activeName = activeWorkspace.name?.trim() || "Workspace";
    const openMenu = (event) => setAnchorEl(event.currentTarget);
    const closeMenu = () => setAnchorEl(null);
    const selectWorkspace = (workspaceId) => {
        closeMenu();
        onSelect?.(workspaceId);
    };

    return (
        <>
            <Box sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                px: collapsed ? 0 : "12px",
                pb: "12px",
                boxSizing: "border-box",
            }}>
                {collapsed ? (
                    <Tooltip title={activeName} placement="right">
                        <IconButton
                            aria-label={`Switch workspace. Current workspace: ${activeName}`}
                            onClick={openMenu}
                            sx={{
                                width: "40px",
                                height: "40px",
                                padding: "4px",
                                borderRadius: "9px",
                                "&:hover": {backgroundColor: "var(--app-bg-subtle)"},
                            }}
                        >
                            <WorkspaceAvatar name={activeName} size={32}/>
                        </IconButton>
                    </Tooltip>
                ) : (
                    <ButtonBase
                        aria-label={`Switch workspace. Current workspace: ${activeName}`}
                        onClick={openMenu}
                        sx={{
                            width: "100%",
                            minWidth: 0,
                            display: "flex",
                            justifyContent: "flex-start",
                            gap: "10px",
                            padding: "7px 9px",
                            border: "1px solid var(--app-border-subtle)",
                            borderRadius: "10px",
                            color: "var(--app-text-primary)",
                            "&:hover": {backgroundColor: "var(--app-bg-subtle)"},
                        }}
                    >
                        <WorkspaceAvatar name={activeName}/>
                        <Typography sx={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textAlign: "left",
                            fontFamily: "Inter",
                            fontSize: "14px",
                            fontWeight: 600,
                        }}>
                            {activeName}
                        </Typography>
                        <KeyboardArrowDownIcon sx={{fontSize: "18px", color: "var(--app-text-muted)"}}/>
                    </ButtonBase>
                )}
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={closeMenu}
                anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                transformOrigin={{vertical: "top", horizontal: "left"}}
                PaperProps={{
                    sx: {
                        minWidth: "240px",
                        mt: "6px",
                        border: "1px solid var(--app-border-subtle)",
                        borderRadius: "10px",
                        backgroundColor: "var(--app-bg-surface)",
                        backgroundImage: "none",
                        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.14)",
                    },
                }}
            >
                {supportedWorkspaces.map((workspace) => {
                    const name = workspace.name?.trim() || "Workspace";
                    const selected = workspace.workspaceId === activeWorkspace.workspaceId;
                    return (
                        <MenuItem
                            key={workspace.workspaceId}
                            selected={selected}
                            onClick={() => selectWorkspace(workspace.workspaceId)}
                            sx={{gap: "10px", py: "8px"}}
                        >
                            <WorkspaceAvatar name={name}/>
                            <Box sx={{flex: 1, minWidth: 0}}>
                                <Typography noWrap sx={{fontFamily: "Inter", fontSize: "14px", fontWeight: 600}}>
                                    {name}
                                </Typography>
                                <Typography sx={{fontFamily: "Inter", fontSize: "12px", color: "var(--app-text-muted)"}}>
                                    {workspace.role === "owner" ? "Owner" : "Editor"}
                                </Typography>
                            </Box>
                            {selected && <CheckIcon sx={{fontSize: "18px", color: "#2B6BFD"}}/>}
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};

export default WorkspaceSwitcher;
